import cx from 'classnames';
import type { FunctionComponent } from 'react';

import { defineSlotComponent, findAllForSlot, findFirstForSlot } from '../../layouts/slots';

import styles from './Card.module.less';

export type Props = {
  variant?: 'outline' | 'fill';
  isFullWidth?: boolean;
  isContentFullHeight?: boolean;
  padding?: 'none' | 'xs' | 'small' | 'medium' | 'large';
  /**
   * Background color. Applies to either variant and takes precedence over the variant's default background
   * (`outline` white, `fill` gray); the variant still controls the border. If omitted, the variant default
   * is used.
   */
  color?: 'blue' | 'pink' | 'yellow' | 'green' | 'gray' | 'dark-gray';
};

type CardComponent = FunctionComponent<Props> & {
  Header: FunctionComponent;
  SubHeader: FunctionComponent;
  Content: FunctionComponent;
};

/**
 * A simple Card component to give us a standardized style of contents.
 *
 * @example
 * <Card>
 *  <Card.Header>The title of my card!</Card.Header>
 *  <Card.SubHeader>The subheader of my card!</Card.SubHeader>
 *  <Card.Content><MyArbitraryComponent /></Card.Content>
 * </Card>
 */
const Card: CardComponent = ({
  variant = 'outline',
  isFullWidth,
  children,
  isContentFullHeight,
  color,
  padding = 'large',
}) => {
  const header = findFirstForSlot('header', children);
  const subheader = findFirstForSlot('subheader', children);
  const content = findAllForSlot('content', children);

  return (
    <div
      className={cx(styles.card, {
        [styles.outline]: variant === 'outline',
        [styles.fill]: variant === 'fill',
        [styles.fitContent]: !isFullWidth,
        [styles.fullWidth]: isFullWidth,
        [styles.blue]: color === 'blue',
        [styles.pink]: color === 'pink',
        [styles.yellow]: color === 'yellow',
        [styles.green]: color === 'green',
        [styles.gray]: color === 'gray',
        [styles.darkGray]: color === 'dark-gray',
        [styles.paddingNone]: padding === 'none',
        [styles.paddingXS]: padding === 'xs',
        [styles.paddingSmall]: padding === 'small',
        [styles.paddingMedium]: padding === 'medium',
        [styles.paddingLarge]: padding === 'large',
      })}
    >
      {header && (
        <div className={styles.header}>
          {header}
          {subheader && <div className={styles.subheader}>{subheader}</div>}
        </div>
      )}
      <div
        className={cx(styles.content, {
          [styles.fullHeight]: !!isContentFullHeight,
        })}
        >
        {content}
      </div>
    </div>
  );
};

Card.Header = defineSlotComponent('header', ({ children }) =>
  <>{children}</>
);
Card.Header.displayName = 'Card.Header';

Card.SubHeader = defineSlotComponent('subheader', ({ children }) =>
  <>{children}</>
);
Card.SubHeader.displayName = 'Card.SubHeader';

Card.Content = defineSlotComponent('content', ({ children }) =>
  <>{children}</>
);
Card.Content.displayName = 'Card.Content';

export default Card;
